import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, TrendingUp, TrendingDown, LineChart as LineChartIcon } from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceArea,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ladditionAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { holidayAreas } from '@/lib/martiniqueCalendar';

const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const MONTHS_ABBR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const monthLabel = m => `${MONTHS_FR[Number(m.split('-')[1]) - 1]} ${m.split('-')[0]}`;

const SERIES_COLORS = {
    'Emporter': '#047857',
    'Sur place': '#8b5cf6',
    'Dood': '#22c55e',
    'Easy': '#a3e635',
    'Total': '#0f172a',
};
const SERIES_KEYS = ['Emporter', 'Sur place', 'Dood', 'Easy'];
const SERIES_TESTIDS = { 'Emporter': 'emporter', 'Sur place': 'surplace', 'Dood': 'dood', 'Easy': 'easy' };

const PERIODS = [
    { id: 'day', label: 'Journée', subtitle: 'vs même jour S-1' },
    { id: 'week', label: 'Hebdomadaire', subtitle: 'vs semaine N-1' },
    { id: 'month', label: 'Mensuel', subtitle: 'vs mois N-1' },
];

const HISTORY_START = { year: 2026, month: 9 }; // fallback si aucune donnée

export default function MyFoodFinancialTab() {
    const [summary, setSummary] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [chartLoading, setChartLoading] = useState(true);
    const [scope, setScope] = useState('week');
    const [chartMode, setChartMode] = useState('daily');
    const [anchorDate, setAnchorDate] = useState(todayIso());
    const [visible, setVisible] = useState({ 'Emporter': true, 'Sur place': true, 'Dood': true, 'Easy': true, 'Total': true });

    useEffect(() => {
        ladditionAPI.financialSummary().then(r => {
            setSummary(r.data);
            if (r.data?.latest_date) {
                setAnchorDate(prev => prev === todayIso() ? r.data.latest_date : prev);
            }
        }).catch(() => {});
    }, []);

    const { startDate, endDate, label } = useMemo(() => {
        if (scope === 'year') {
            const y = parseInt(anchorDate.slice(0, 4), 10);
            return { startDate: `${y}-01-01`, endDate: `${y}-12-31`, label: `Année ${y}` };
        }
        if (scope === 'month') {
            const [y, m] = anchorDate.split('-').map(Number);
            const lastDay = new Date(y, m, 0).getDate();
            return {
                startDate: `${y}-${String(m).padStart(2, '0')}-01`,
                endDate: `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
                label: `${MONTHS_ABBR[m - 1]} ${y}`,
            };
        }
        const d = new Date(anchorDate + 'T00:00:00');
        const dow = d.getDay();
        const diff = dow === 0 ? -6 : 1 - dow;
        const mon = new Date(d); mon.setDate(d.getDate() + diff);
        const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
        return {
            startDate: toIso(mon),
            endDate: toIso(sun),
            label: `S${isoWeekNumber(mon)} (${toIso(mon).slice(8, 10)}/${toIso(mon).slice(5, 7)} → ${toIso(sun).slice(8, 10)}/${toIso(sun).slice(5, 7)})`,
        };
    }, [scope, anchorDate]);

    useEffect(() => {
        setChartLoading(true);
        ladditionAPI.dailyCa(startDate, endDate)
            .then(r => setChartData(r.data))
            .finally(() => setChartLoading(false));
    }, [startDate, endDate]);

    const aggregatedSeries = useMemo(() => {
        if (!chartData?.series) return [];
        if (scope !== 'year') return chartData.series;
        const buckets = {};
        for (const row of chartData.series) {
            const ym = row.date.slice(0, 7);
            if (!buckets[ym]) buckets[ym] = { date: `${ym}-15` };
            for (const k of Object.keys(row)) {
                if (k === 'date' || row[k] === null || row[k] === undefined) continue;
                buckets[ym][k] = Math.round(((buckets[ym][k] || 0) + row[k]) * 100) / 100;
            }
        }
        return Object.values(buckets).sort((a, b) => a.date.localeCompare(b.date));
    }, [chartData, scope]);

    const displaySeries = useMemo(() => {
        if (!aggregatedSeries.length || chartMode === 'daily') return aggregatedSeries;
        const acc = {};
        return aggregatedSeries.map(row => {
            const out = { date: row.date };
            for (const k of Object.keys(row)) {
                if (k === 'date') continue;
                if (row[k] === null || row[k] === undefined) {
                    out[k] = acc[k] !== undefined ? Math.round(acc[k] * 100) / 100 : null;
                } else {
                    acc[k] = (acc[k] || 0) + row[k];
                    out[k] = Math.round(acc[k] * 100) / 100;
                }
            }
            return out;
        });
    }, [aggregatedSeries, chartMode]);

    const toggle = (name) => setVisible(v => ({ ...v, [name]: !v[name] }));

    const historyStart = useMemo(() => {
        if (summary?.first_date) {
            const [y, m] = summary.first_date.split('-').map(Number);
            return { year: y, month: m };
        }
        return HISTORY_START;
    }, [summary]);
    const weekShortcuts = useMemo(() => buildWeekShortcuts(historyStart), [historyStart]);
    const monthShortcuts = useMemo(() => buildMonthShortcuts(historyStart), [historyStart]);
    const yearShortcuts = useMemo(() => buildYearShortcuts(historyStart), [historyStart]);
    const activeKey = scope === 'month' ? anchorDate.slice(0, 7) : scope === 'year' ? anchorDate.slice(0, 4) : startDate;
    const shortcuts = scope === 'week' ? weekShortcuts : scope === 'month' ? monthShortcuts : yearShortcuts;
    const scrollerRef = useRef(null);
    useEffect(() => {
        const raf = requestAnimationFrame(() => {
            const container = scrollerRef.current;
            if (!container) return;
            const el = container.querySelector(`[data-shortcut-key="${activeKey}"]`);
            if (!el) return;
            const contRect = container.getBoundingClientRect();
            const elRect = el.getBoundingClientRect();
            container.scrollLeft = Math.max(0, container.scrollLeft + (elRect.left - contRect.left) - container.clientWidth / 2 + el.clientWidth / 2);
        });
        return () => cancelAnimationFrame(raf);
    }, [scope, activeKey, summary]);

    return (
        <div className="space-y-3" data-testid="myfood-financial-tab">
            <Card className={scope === 'week' ? 'lg:w-2/3' : ''}>
                <CardHeader className="py-2 px-3 border-b space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <LineChartIcon className="w-4 h-4 text-rose-500" />
                        <CardTitle className="text-sm">Évolution du CA TTC</CardTitle>
                        <Badge variant="outline" className="text-[10px] font-mono text-slate-500">{label}</Badge>
                        <div className="inline-flex bg-slate-200 rounded-md p-0.5 text-[11px] gap-0.5">
                            {[['year', 'Année'], ['month', 'Mois'], ['week', 'Semaine']].map(([id, lbl]) => (
                                <button key={id} type="button" onClick={() => setScope(id)}
                                    className={`px-2.5 py-0.5 rounded font-semibold transition-all ${scope === id ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                                    data-testid={`mf-scope-${id}`}
                                >{lbl}</button>
                            ))}
                        </div>
                        <div className="ml-auto flex items-center gap-2">
                            <div className="inline-flex bg-slate-200 rounded-md p-0.5 text-[11px] gap-0.5">
                                <button type="button" onClick={() => setChartMode('daily')}
                                    className={`px-2.5 py-0.5 rounded font-semibold transition-all ${chartMode === 'daily' ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                                    data-testid="mf-chart-mode-daily"
                                >Quotidien</button>
                                <button type="button" onClick={() => setChartMode('cumul')}
                                    className={`px-2.5 py-0.5 rounded font-semibold transition-all ${chartMode === 'cumul' ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                                    data-testid="mf-chart-mode-cumul"
                                >Cumulé</button>
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5" data-testid="mf-chart-toggles">
                                {SERIES_KEYS.map(name => {
                                    const checked = !!visible[name];
                                    const color = SERIES_COLORS[name];
                                    return (
                                        <label key={name}
                                            className={`inline-flex items-center gap-1 px-2 h-6 rounded-full border cursor-pointer transition-colors text-[11px] font-semibold ${checked ? 'bg-white border-slate-300' : 'bg-slate-50 border-slate-200 text-slate-400'}`}
                                            style={{ borderColor: checked ? color : undefined, color: checked ? color : undefined }}
                                        >
                                            <Checkbox checked={checked} onCheckedChange={() => toggle(name)} className="w-3 h-3"
                                                data-testid={`mf-chart-toggle-${SERIES_TESTIDS[name]}`} />
                                            {name}
                                        </label>
                                    );
                                })}
                                <label className={`inline-flex items-center gap-1 px-2 h-6 rounded-full border cursor-pointer text-[11px] font-semibold ${visible.Total ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>
                                    <Checkbox checked={!!visible.Total} onCheckedChange={() => toggle('Total')} className="w-3 h-3" data-testid="mf-chart-toggle-total" />
                                    Total
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <button type="button" onClick={() => { if (scrollerRef.current) scrollerRef.current.scrollLeft -= 220; }}
                            className="h-6 w-5 flex-shrink-0 text-slate-400 hover:text-slate-700 text-sm" title="Reculer">‹</button>
                        <div ref={scrollerRef} className="flex-1 overflow-x-auto" style={{ scrollBehavior: 'smooth' }}>
                            <div className="flex gap-1 min-w-max pb-0.5">
                                {shortcuts.map(s => {
                                    const active = s.key === activeKey;
                                    return (
                                        <button key={s.key} type="button" onClick={() => setAnchorDate(s.anchor)}
                                            data-shortcut-key={s.key}
                                            className={`shrink-0 px-2 h-6 rounded-md border text-[11px] font-semibold transition-colors ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                                            data-testid={`mf-shortcut-${s.key}`}
                                        >{s.label}</button>
                                    );
                                })}
                            </div>
                        </div>
                        <button type="button" onClick={() => { if (scrollerRef.current) scrollerRef.current.scrollLeft += 220; }}
                            className="h-6 w-5 flex-shrink-0 text-slate-400 hover:text-slate-700 text-sm" title="Avancer">›</button>
                    </div>
                </CardHeader>
                <CardContent className="p-2">
                    {chartLoading ? (
                        <div className="h-72 flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
                    ) : (
                        <ResponsiveContainer width="100%" height={320}>
                            <LineChart data={displaySeries} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                {scope !== 'year' && holidayAreas(displaySeries.map(r => r.date)).map((a, i) => (
                                    <ReferenceArea key={i} x1={a.x1} x2={a.x2} fill="#38bdf8" fillOpacity={0.07}
                                        label={{ value: `Vac. ${a.label}`, position: 'insideTopLeft', fontSize: 9, fill: '#0284c7', opacity: 0.8 }} />
                                ))}
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={(v) => scope === 'year' ? (MONTHS_ABBR[parseInt(v.slice(5, 7), 10) - 1] || v) : `${v.slice(8, 10)}/${v.slice(5, 7)}`}
                                    tick={(props) => {
                                        const { x, y, payload } = props;
                                        const isYear = scope === 'year';
                                        const labelText = isYear
                                            ? (MONTHS_ABBR[parseInt(payload.value.slice(5, 7), 10) - 1] || payload.value)
                                            : `${payload.value.slice(8, 10)}/${payload.value.slice(5, 7)}`;
                                        let dowLetter = '', dowFill = '#94a3b8', dowWeight = 'normal';
                                        if (!isYear) {
                                            const d = new Date(payload.value + 'T00:00:00');
                                            const dayIdx = d.getDay();
                                            dowLetter = ['D', 'L', 'M', 'M', 'J', 'V', 'S'][dayIdx];
                                            if (dayIdx === 0 || dayIdx === 6) { dowFill = '#f87171'; dowWeight = 'bold'; }
                                        }
                                        return (
                                            <g transform={`translate(${x},${y})`}>
                                                <text x={0} y={0} dy={12} textAnchor="middle" fontSize={11} fill="#64748b">{labelText}</text>
                                                {!isYear && <text x={0} y={0} dy={22} textAnchor="middle" fontSize={9} fill={dowFill} fontWeight={dowWeight}>{dowLetter}</text>}
                                            </g>
                                        );
                                    }}
                                    interval={scope === 'week' || scope === 'year' ? 0 : 'preserveStartEnd'}
                                    height={scope === 'year' ? 30 : 36}
                                />
                                <YAxis tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} tick={{ fontSize: 11, fill: '#64748b' }} width={50} />
                                <Tooltip
                                    contentStyle={{ fontSize: 11, borderRadius: 6 }}
                                    content={({ active, payload, label: tLabel }) => {
                                        if (!active || !payload?.length) return null;
                                        const d = new Date(tLabel + 'T00:00:00');
                                        const dayIdx = d.getDay();
                                        const dow = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][dayIdx];
                                        const isWeekend = dayIdx === 0 || dayIdx === 6;
                                        return (
                                            <div className="bg-white border rounded-md shadow-lg p-2 text-[11px] max-w-[280px]">
                                                <div className={`font-semibold mb-1 ${isWeekend ? 'text-red-500' : 'text-slate-700'}`}>
                                                    {scope === 'year' ? monthLabel(tLabel.slice(0, 7)) : `${dow} ${tLabel}`}
                                                </div>
                                                {payload.filter(p => p.value !== null && p.value !== undefined).map(p => (
                                                    <div key={p.dataKey} className="flex items-center justify-between gap-2">
                                                        <span style={{ color: p.color }}>● {p.dataKey}</span>
                                                        <span className="tabular-nums font-semibold">{formatCurrency(p.value)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    }}
                                />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                {SERIES_KEYS.map(name => visible[name] && (
                                    <Line key={name} type="monotone" dataKey={name}
                                        stroke={SERIES_COLORS[name]} strokeWidth={2}
                                        dot={{ r: 2 }} activeDot={{ r: 4 }} isAnimationActive={false} connectNulls={false} />
                                ))}
                                {visible.Total && (
                                    <Line type="monotone" dataKey="Total" stroke={SERIES_COLORS.Total}
                                        strokeWidth={2.5} strokeDasharray="4 4" dot={{ r: 2 }} activeDot={{ r: 4 }}
                                        isAnimationActive={false} connectNulls={false} />
                                )}
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </CardContent>
            </Card>

            {summary?.periods && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    {PERIODS.map(p => <PeriodCard key={p.id} period={p} block={summary.periods[p.id]} />)}
                </div>
            )}
            <div className="text-[11px] text-slate-400 italic">
                CA TTC des rapports Z L'Addition. Emporter = articles VAE, Sur place = autres articles, Total = CA TTC déclaré du Z.
                La « Journée » affiche le dernier rapport Z reçu, comparé au même jour de la semaine précédente.
            </div>
        </div>
    );
}

function PeriodCard({ period, block }) {
    if (!block) return null;
    const { label_current, label_previous, rows, totals } = block;
    return (
        <Card className="overflow-hidden" data-testid={`mf-period-card-${period.id}`}>
            <CardHeader className="py-2 px-3 bg-gradient-to-r from-slate-50 to-white border-b">
                <div className="flex items-baseline justify-between">
                    <div>
                        <CardTitle className="text-sm font-semibold text-slate-800">{period.label}</CardTitle>
                        <div className="text-[10px] text-slate-400 mt-0.5">{period.subtitle}</div>
                    </div>
                    <Badge variant="outline" className="text-[10px] font-mono text-slate-500 border-slate-200">{label_current}</Badge>
                </div>
            </CardHeader>
            <CardContent className="p-0">
                <div className="px-3 py-2 bg-slate-900 text-white">
                    <div className="flex items-baseline justify-between">
                        <span className="text-[11px] uppercase tracking-wider text-slate-400">Total TTC {totals.couverts ? `· ${totals.couverts} couverts` : ''}</span>
                        <DeltaBadge value={totals.delta_ca_pct} inverted />
                    </div>
                    <div className="flex items-baseline gap-2 mt-0.5">
                        <span className="text-2xl font-bold tabular-nums">{formatCurrency(totals.ca)}</span>
                        <span className="text-[11px] text-slate-400 tabular-nums">vs {formatCurrency(totals.ca_prev)}</span>
                    </div>
                </div>
                <table className="w-full text-[11px]">
                    <thead className="bg-slate-50 border-b text-[10px] uppercase text-slate-500">
                        <tr>
                            <th className="text-left px-3 py-1">Service</th>
                            <th className="text-right px-3 py-1">CA TTC</th>
                            <th className="text-right px-3 py-1">Δ</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.code} className="border-b border-slate-100 hover:bg-slate-50/60">
                                <td className="px-3 py-1 font-medium" style={{ color: SERIES_COLORS[r.label] || '#334155' }}>{r.label}</td>
                                <td className="px-3 py-1 text-right tabular-nums font-semibold text-slate-800">
                                    {r.ca > 0 ? formatCurrency(r.ca) : <span className="text-slate-300">—</span>}
                                </td>
                                <td className="px-3 py-1 text-right"><DeltaBadge value={r.delta_ca_pct} compact /></td>
                            </tr>
                        ))}
                        {block.platforms && ['Dood', 'Easy'].map(mode => {
                            const p = block.platforms[mode];
                            if (!p) return null;
                            return (
                                <tr key={mode} className="border-b border-slate-100 bg-emerald-50/40" data-testid={`mf-platform-row-${mode.toLowerCase()}-${period.id}`}>
                                    <td className="px-3 py-1 pl-6 text-emerald-700 font-medium text-[10px]">
                                        dont {mode} <span className="text-slate-400 font-normal">(emporter)</span>
                                    </td>
                                    <td className="px-3 py-1 text-right tabular-nums font-semibold text-emerald-800">
                                        {p.ca > 0 ? formatCurrency(p.ca) : <span className="text-slate-300">—</span>}
                                    </td>
                                    <td className="px-3 py-1 text-right"><DeltaBadge value={p.delta_ca_pct} compact /></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <div className="px-3 py-1.5 bg-slate-50 text-[10px] text-slate-500 flex justify-between border-t">
                    <span>Période N-1 : {label_previous}</span>
                    <span className="tabular-nums">{totals.delta_ca_abs > 0 ? '+' : ''}{formatCurrency(totals.delta_ca_abs)}</span>
                </div>
            </CardContent>
        </Card>
    );
}

function DeltaBadge({ value, compact = false, inverted = false }) {
    if (value === null || value === undefined) {
        return <span className={`text-[10px] ${inverted ? 'text-slate-400' : 'text-slate-300'}`}>—</span>;
    }
    const positive = value > 0;
    const neutral = Math.abs(value) < 0.5;
    let cls;
    if (neutral) cls = inverted ? 'text-slate-300' : 'bg-slate-100 text-slate-500';
    else if (positive) cls = inverted ? 'text-emerald-300' : 'bg-emerald-100 text-emerald-700';
    else cls = inverted ? 'text-red-300' : 'bg-red-100 text-red-700';
    const Icon = neutral ? null : positive ? TrendingUp : TrendingDown;
    return (
        <span className={`inline-flex items-center gap-0.5 ${inverted ? '' : 'px-1.5 py-0.5 rounded-full'} text-[10px] font-semibold tabular-nums ${cls}`}>
            {Icon && <Icon className="w-2.5 h-2.5" />}
            {positive ? '+' : ''}{value.toFixed(1)}%
        </span>
    );
}

function todayIso() { return new Date().toISOString().slice(0, 10); }
function toIso(d) { return d.toISOString().slice(0, 10); }
function isoWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function buildMonthShortcuts(start) {
    const today = new Date();
    const arr = [];
    let y = start.year, m = start.month, lastYear = null;
    while (y < today.getFullYear() || (y === today.getFullYear() && m <= today.getMonth() + 1)) {
        const key = `${y}-${String(m).padStart(2, '0')}`;
        const showYear = (m === 1) || lastYear === null || lastYear !== y;
        lastYear = y;
        arr.push({ key, anchor: `${key}-15`, label: showYear ? `${MONTHS_ABBR[m - 1]} ${String(y).slice(2)}` : MONTHS_ABBR[m - 1] });
        m += 1;
        if (m > 12) { m = 1; y += 1; }
    }
    return arr;
}

function buildYearShortcuts(start) {
    const today = new Date();
    const arr = [];
    for (let y = start.year; y <= today.getFullYear(); y++) {
        arr.push({ key: String(y), anchor: `${y}-06-15`, label: String(y) });
    }
    return arr;
}

function buildWeekShortcuts(start) {
    const today = new Date();
    const dow = today.getDay();
    const diff = dow === 0 ? -6 : 1 - dow;
    const currentMonday = new Date(today); currentMonday.setDate(today.getDate() + diff);
    let startD = new Date(start.year, start.month - 1, 1);
    const sdow = startD.getDay();
    startD.setDate(startD.getDate() + (sdow === 0 ? -6 : 1 - sdow));
    const arr = [];
    let lastMonthLabel = null;
    const cur = new Date(startD);
    while (cur <= currentMonday) {
        const w = isoWeekNumber(cur);
        const mLabel = `${MONTHS_ABBR[cur.getMonth()]} ${String(cur.getFullYear()).slice(2)}`;
        const showMonth = mLabel !== lastMonthLabel;
        lastMonthLabel = mLabel;
        arr.push({ key: toIso(cur), anchor: toIso(cur), label: showMonth ? `S${w} · ${mLabel}` : `S${w}` });
        cur.setDate(cur.getDate() + 7);
    }
    return arr;
}
